import {
  QuizDiagnosticsBookGroup,
  QuizDiagnosticsChapter,
  QuizDiagnosticsSection,
  QuizDiagnosticsTagColor
} from '../types'
import {
  QuizDiagnosticsApiBook,
  QuizDiagnosticsApiChapter,
  QuizDiagnosticsApiResponse,
  QuizDiagnosticsApiSection
} from '../types/api'
import { sortChaptersByTag } from './sortChaptersByTag'

const TAG_COLOURS: QuizDiagnosticsTagColor[] = [
  'purple',
  'red',
  'blue',
  'green',
  'orange',
  'brown',
  'mathPurple',
  'aquamarine',
  'turquoise',
  'yellow',
  'grey',
  'terraCotta',
  'gold',
  'tangerine',
  'guacamole',
  'ultramarine',
  'grape',
  'moss',
  'slate'
]

const normalizeTagColour = (
  colour?: string | QuizDiagnosticsTagColor
): QuizDiagnosticsTagColor =>
  TAG_COLOURS.includes(colour as QuizDiagnosticsTagColor)
    ? (colour as QuizDiagnosticsTagColor)
    : 'grey'

const mapSection = (section?: QuizDiagnosticsApiSection): QuizDiagnosticsSection => ({
  id: section?.student_book_subchapter_id ?? '',
  title: section?.section_title ?? '',
  fiveQuizAverage: section?.five_quiz_average ?? 0,
  previousFiveQuizAverage: section?.previous_five_quiz_average ?? 0,
  totalQuizzesTaken: section?.total_quizzes_taken ?? 0,
  hasQuizDueToday: section?.has_quiz_due_today,
  srqRedDotsVisible: section?.srq_red_dots_visible ?? true
})

const mapChapter = (
  chapter?: QuizDiagnosticsApiChapter,
  book?: QuizDiagnosticsApiBook
): QuizDiagnosticsChapter => ({
  id: chapter?.student_book_chapter_id ?? '',
  student_book_id: book?.student_book_id ?? chapter?.student_book_id ?? '',
  tag: book?.tag ?? '',
  tag_colour: normalizeTagColour(book?.tag_colour),
  order: chapter?.chapter_order ?? 0,
  average_score: chapter?.chapter_average ?? 0,
  quizzes_due_today: chapter?.quizzes_due_today ?? 0,
  srqRedDotsVisible: chapter?.srq_red_dots_visible ?? true,
  title: chapter?.chapter_title ?? '',
  book_title: book?.book_title ?? '',
  sections: (chapter?.sections ?? []).map(section => mapSection(section))
})

const mapBookGroup = (book?: QuizDiagnosticsApiBook): QuizDiagnosticsBookGroup => {
  const chapters = (book?.chapters ?? [])
    .map(chapter => mapChapter(chapter, book))
    .sort((a, b) => a.order - b.order)

  return {
    bookId: book?.student_book_id ?? '',
    tag: book?.tag ?? '',
    tag_colour: normalizeTagColour(book?.tag_colour),
    chapters
  }
}

export type MappedQuizDiagnostics = {
  srqEnabled: boolean
  srqRedDotsVisible: boolean
  bookGroups: QuizDiagnosticsBookGroup[]
  overallPercent: number
  totalQuizzes: number
  hasData: boolean
}

export const mapQuizDiagnosticsApiResponse = (
  apiResponse?: QuizDiagnosticsApiResponse | null
): MappedQuizDiagnostics => {
  const srqEnabled = apiResponse?.srq_enabled === true

  if (!srqEnabled) {
    return {
      srqEnabled: false,
      srqRedDotsVisible: true,
      bookGroups: [],
      overallPercent: 0,
      totalQuizzes: 0,
      hasData: false
    }
  }

  const bookGroups = sortChaptersByTag(
    (apiResponse?.books ?? [])
      .map(book => mapBookGroup(book))
      .filter(book => book.chapters.length > 0)
  )

  const totalQuizzes = apiResponse?.total_quizzes_taken ?? 0
  const overallPercent = apiResponse?.course_average ?? 0
  const hasChapters = bookGroups.some(book => book.chapters.length > 0)

  return {
    srqEnabled: true,
    srqRedDotsVisible: apiResponse?.srq_red_dots_visible ?? true,
    bookGroups,
    overallPercent,
    totalQuizzes,
    hasData: hasChapters || totalQuizzes > 0
  }
}

export const flattenBookGroupChapters = (
  bookGroups: QuizDiagnosticsBookGroup[] = []
): QuizDiagnosticsChapter[] => bookGroups.flatMap(book => book.chapters ?? [])

export const filterBookGroupsByChapterId = (
  bookGroups: QuizDiagnosticsBookGroup[],
  chapterId: string
): QuizDiagnosticsBookGroup[] => {
  if (chapterId === 'all') {
    return bookGroups
  }

  return bookGroups
    .map(book => ({
      ...book,
      chapters: book.chapters.filter(chapter => chapter.id === chapterId)
    }))
    .filter(book => book.chapters.length > 0)
}

export const getFilteredOverallPercent = (
  bookGroups: QuizDiagnosticsBookGroup[],
  chapterId: string,
  courseOverallPercent: number
): number => {
  if (chapterId === 'all') {
    return courseOverallPercent
  }

  const chapter = flattenBookGroupChapters(bookGroups).find(
    item => item.id === chapterId
  )

  return chapter?.average_score ?? 0
}
